/* gp-pdp.js — variant selection, gallery, and sticky ATC for the gp-main-product section.
   Add-to-cart itself is handled by the theme's own <product-form> element (AJAX + cart drawer);
   this script only drives the custom selection UI and keeps the hidden variant id in sync. */
(function () {
  var DOTS_VISIBLE = 6;   // dots that fit in the mobile window (see .gp-gallery__dots-win)
  var DOT_STEP = 15;      // 8px dot + 7px gap
  var STICKY_GAP = 20;    // fallback space above the pinned gallery (the section normally passes its own)
  var STICKY_HOLD = 300;  // fallback minimum hold for the desktop gallery
  var STICKY_BOTTOM = 20; // breathing room under the gallery when it is taller than the viewport

  function money(cents, format) {
    var v = (cents / 100);
    // Match Shopify money format loosely: use provided formatted strings from data instead when available.
    return v;
  }

  class GpProduct extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.variants = JSON.parse(this.querySelector('[data-gp-variants]').textContent);
      this.colorIdx = this.hasAttribute('data-color-index') ? parseInt(this.getAttribute('data-color-index'), 10) : -1;
      this.sizeIdx = this.hasAttribute('data-size-index') ? parseInt(this.getAttribute('data-size-index'), 10) : -1;

      this.form = this.querySelector('.gp-product-form');
      this.idInput = this.querySelector('.gp-current-variant-id');
      this.priceSale = this.querySelector('[data-gp-price-sale]');
      this.priceCompare = this.querySelector('[data-gp-price-compare]');
      this.atcBtn = this.querySelector('[data-gp-atc]');
      this.urgencyEl = this.querySelector('[data-gp-urgency]');
      this.mainImages = Array.prototype.slice.call(this.querySelectorAll('.gp-gallery__slide'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('.gp-gallery__thumb'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.gp-gallery__dot'));
      this.dotsWin = this.querySelector('[data-gp-dots-win]');
      this.galleryTrack = this.querySelector('.gp-gallery__main');
      this.gallery = this.querySelector('.gp-product__gallery.is-sticky');
      this.info = this.querySelector('.gp-product__info');
      this.swatches = Array.prototype.slice.call(this.querySelectorAll('.gp-swatch'));
      this.sizeBtns = Array.prototype.slice.call(this.querySelectorAll('.gp-size'));

      // initial selection = first available variant, else first variant
      var initial = this.variants.filter(function (v) { return v.available; })[0] || this.variants[0];
      this.selected = initial.options.slice();

      this.bindRatingLink();
      this.bindGallery();
      this.bindGallerySwipe();
      this.bindLightbox();
      this.syncDots();
      this.bindSwatches();
      this.bindSizes();
      this.bindSticky();
      this.bindStickyGallery();
      this.update();
    }

    variantFor(opts) {
      return this.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      });
    }

    availableForOption(idx, value) {
      // is there ANY available variant with this option value, honoring the currently-selected color (for sizes)
      var sel = this.selected;
      return this.variants.some(function (v) {
        if (v.options[idx] !== value) return false;
        if (idx === this.sizeIdx && this.colorIdx > -1 && v.options[this.colorIdx] !== sel[this.colorIdx]) return false;
        return v.available;
      }, this);
    }

    bindGallery() {
      var self = this;
      this.thumbs.forEach(function (t) {
        t.addEventListener('click', function () { self.activateMediaById(t.getAttribute('data-media-id')); });
      });
      this.dots.forEach(function (d) {
        d.addEventListener('click', function () { self.activateMediaById(d.getAttribute('data-media-id')); });
      });
    }

    activateMediaById(mediaId) {
      if (!mediaId) return;
      var self = this;
      this.mainImages.forEach(function (s) { s.classList.toggle('is-active', s.getAttribute('data-media-id') === mediaId); });
      this.thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-media-id') === mediaId); });
      this.dots.forEach(function (d) { d.classList.toggle('is-active', d.getAttribute('data-media-id') === mediaId); });
      this.scrollTrackToActive();
      var activeThumb = this.querySelector('.gp-gallery__thumb.is-active');
      if (activeThumb && activeThumb.scrollIntoView) activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      this.syncDots();
    }

    /* Mobile renders the same markup as a horizontal scroll-snap track, desktop as a stack
       with only .is-active visible. One test tells them apart without repeating the
       breakpoint in JS: only the track ever overflows horizontally. */
    carouselActive() {
      var t = this.galleryTrack;
      return !!t && t.scrollWidth > t.clientWidth + 1;
    }

    /* Bring the track to the active slide after a dot, thumb or swatch click. Deliberately
       instant: scroll-behavior: smooth makes a programmatic scrollLeft a no-op in some
       engines (same trap as the dots window). */
    scrollTrackToActive() {
      if (!this.carouselActive()) return;
      var idx = -1;
      this.mainImages.forEach(function (s, i) { if (s.classList.contains('is-active')) idx = i; });
      if (idx < 0) return;
      var left = idx * this.galleryTrack.clientWidth;
      if (Math.abs(this.galleryTrack.scrollLeft - left) < 2) return;
      this.galleryTrack.scrollLeft = left;
    }

    /* Swiping IS the browser's own scrolling — nothing here drives it. This only reads back
       which photo came to rest in view and moves the dots to match. */
    bindGallerySwipe() {
      if (!this.galleryTrack) return;
      var self = this;

      // Synchronous on purpose: the index check below is the throttle, so a dropped
      // requestAnimationFrame can never leave this handler wedged mid-swipe.
      this.galleryTrack.addEventListener('scroll', function () {
        if (!self.carouselActive()) return;
        var idx = Math.round(self.galleryTrack.scrollLeft / self.galleryTrack.clientWidth);
        idx = Math.min(Math.max(idx, 0), self.mainImages.length - 1);
        var slide = self.mainImages[idx];
        // Already in sync (also the case right after scrollTrackToActive), so no work
        // and — crucially — no scroll back, which would fight the finger.
        if (!slide || slide.classList.contains('is-active')) return;
        var mediaId = slide.getAttribute('data-media-id');
        self.mainImages.forEach(function (s) { s.classList.toggle('is-active', s === slide); });
        self.thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-media-id') === mediaId); });
        self.dots.forEach(function (d) { d.classList.toggle('is-active', d.getAttribute('data-media-id') === mediaId); });
        self.syncDots();
      }, { passive: true });

      // Rotating the phone or crossing the breakpoint changes the slide width; without this
      // the track would be left resting between two photos.
      window.addEventListener('resize', function () {
        if (self._alignRaf) cancelAnimationFrame(self._alignRaf);
        self._alignRaf = requestAnimationFrame(function () { self._alignRaf = 0; self.scrollTrackToActive(); });
      });

      // A variant-specific featured image can make a later slide the initial one.
      this.scrollTrackToActive();
      window.addEventListener('load', function () { self.scrollTrackToActive(); });
    }

    /* Full-screen viewer. The gallery crops every photo into one shared frame so the
       carousel does not jump height; this is where the visitor sees a photo whole. */
    bindLightbox() {
      this.lightbox = this.querySelector('[data-gp-lightbox]');
      if (!this.lightbox) return;
      var self = this;
      this.lbTrack = this.lightbox.querySelector('[data-gp-lightbox-track]');
      this.lbCount = this.lightbox.querySelector('[data-gp-lightbox-count]');
      this.lbSlides = Array.prototype.slice.call(this.lightbox.querySelectorAll('.gp-lightbox__slide'));

      this.mainImages.forEach(function (slide, i) {
        if (slide.classList.contains('gp-gallery__slide--placeholder')) return;
        slide.addEventListener('click', function () { self.openLightbox(i); });
      });

      this.lightbox.querySelector('[data-gp-lightbox-close]')
        .addEventListener('click', function () { self.closeLightbox(); });

      // Same reasoning as bindGallerySwipe: synchronous, with the index check as the
      // throttle, so a dropped frame can never wedge the handler mid-swipe.
      this.lbTrack.addEventListener('scroll', function () {
        var idx = Math.round(self.lbTrack.scrollLeft / self.lbTrack.clientWidth);
        idx = Math.min(Math.max(idx, 0), self.lbSlides.length - 1);
        if (idx === self.lbIndex) return;
        self.unzoomLightbox();
        self.lbIndex = idx;
        self.loadLightboxImages();
        self.syncLightboxCount();
      }, { passive: true });

      // ---- Zoom. Layout-based (the image really gets wider), never transform and never
      // native pinch: a transform can't scroll left of its box, and native pinch zooms
      // the PAGE — inside a fixed overlay that strands the photo against one edge, and
      // the finger drift doubles as pan-x so the snap track flips photos mid-pinch.
      // While zoomed the track is locked; switching photos requires zooming out first.

      var clamp01 = function (v) { return Math.min(Math.max(isFinite(v) ? v : 0.5, 0), 1); };

      // The fitted width is the zoom floor. Cached on first use, before any inline width.
      // Returns 0 for a photo that has not painted yet — callers skip the zoom then,
      // instead of zooming a zero-width box (or caching 0 as the floor forever).
      var fitWidthOf = function (slide, img) {
        if (!img._gpFitW) {
          if (!img.complete || !img.naturalWidth) return 0;
          if (!slide.classList.contains('is-zoomed')) img._gpFitW = img.offsetWidth;
          else {
            var ar = img.naturalWidth / img.naturalHeight;
            img._gpFitW = Math.min(slide.clientWidth, slide.clientHeight * ar);
          }
        }
        return img._gpFitW;
      };

      // Make the image w px wide and scroll so image-point (rx, ry) sits at the anchor
      // (viewport point relative to the slide). offsetWidth forces the layout in between.
      var applyZoom = function (slide, img, w, rx, ry, anchorX, anchorY) {
        // A zoomed image deserves sharper srcset candidates; browsers only ever upgrade.
        if (img.getAttribute('srcset')) img.setAttribute('sizes', '250vw');
        slide.classList.add('is-zoomed');
        self.lbTrack.classList.add('is-locked');
        img.style.width = Math.round(w) + 'px';
        slide.scrollLeft = rx * img.offsetWidth - anchorX;
        slide.scrollTop = ry * img.offsetHeight - anchorY;
      };

      // Double-tap / double-click: zoom to 2.5x fit with the tapped point mid-screen.
      var toggleZoom = function (slide, tapX, tapY) {
        if (!slide) return;
        var img = slide.querySelector('.gp-lightbox__img');
        if (!img) return;
        if (slide.classList.contains('is-zoomed')) { self.unzoomLightbox(); return; }
        var fitW = fitWidthOf(slide, img);
        if (!fitW) return;
        var r = img.getBoundingClientRect();
        var rx = clamp01(r.width ? (tapX - r.left) / r.width : 0.5);
        var ry = clamp01(r.height ? (tapY - r.top) / r.height : 0.5);
        applyZoom(slide, img, fitW * 2.5, rx, ry, slide.clientWidth / 2, slide.clientHeight / 2);
      };
      this.lbTrack.addEventListener('dblclick', function (e) {
        toggleZoom(e.target.closest('.gp-lightbox__slide'), e.clientX, e.clientY);
      });

      // Pinch: continuous zoom around the pinch point, clamped to 1x-4x fit.
      var pinch = null;
      var touchDist = function (t) {
        var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      };
      this.lbTrack.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 2) return;
        var slide = e.target.closest('.gp-lightbox__slide');
        var img = slide && slide.querySelector('.gp-lightbox__img');
        if (!img || !fitWidthOf(slide, img)) return;
        var midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        var midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        var r = img.getBoundingClientRect();
        var sr = slide.getBoundingClientRect();
        pinch = {
          slide: slide, img: img,
          d0: touchDist(e.touches) || 1,
          startW: img.offsetWidth,
          fitW: fitWidthOf(slide, img),
          rx: clamp01(r.width ? (midX - r.left) / r.width : 0.5),
          ry: clamp01(r.height ? (midY - r.top) / r.height : 0.5),
          anchorX: midX - sr.left,
          anchorY: midY - sr.top
        };
        // Two-finger drift must never snap-scroll the track out from under the pinch.
        self.lbTrack.classList.add('is-locked');
      }, { passive: true });
      // Non-passive on purpose: the preventDefault is what keeps the browser's own
      // page zoom out of the gesture entirely.
      this.lbTrack.addEventListener('touchmove', function (e) {
        if (!pinch || e.touches.length !== 2) return;
        e.preventDefault();
        var w = pinch.startW * (touchDist(e.touches) / pinch.d0);
        w = Math.min(Math.max(w, pinch.fitW), pinch.fitW * 4);
        if (w <= pinch.fitW * 1.02) {
          // Back at fit mid-gesture: show the fitted photo but keep the lock until release.
          pinch.slide.classList.remove('is-zoomed');
          pinch.img.style.width = '';
        } else {
          applyZoom(pinch.slide, pinch.img, w, pinch.rx, pinch.ry, pinch.anchorX, pinch.anchorY);
        }
      }, { passive: false });
      var endPinch = function (e) {
        if (!pinch || (e.touches && e.touches.length >= 2)) return;
        var done = pinch; pinch = null;
        // A near-fit release is noise: settle back to the fitted photo (also unlocks).
        if (!done.slide.classList.contains('is-zoomed') || done.img.offsetWidth <= done.fitW * 1.1) {
          self.unzoomLightbox();
        }
      };
      this.lbTrack.addEventListener('touchend', endPinch, { passive: true });
      this.lbTrack.addEventListener('touchcancel', endPinch, { passive: true });

      // Mobile Safari does not fire dblclick reliably on a double-tap, so detect it here.
      // Swipe-down-to-close rides on the same touch pair: a mostly-vertical drag, and only
      // while unzoomed, where a vertical drag has no other meaning.
      var t0 = 0, x0 = 0, y0 = 0, lastTap = 0;
      this.lbTrack.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { t0 = 0; return; }
        t0 = Date.now(); x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      this.lbTrack.addEventListener('touchend', function (e) {
        if (!t0 || e.changedTouches.length !== 1) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        var slide = e.target.closest('.gp-lightbox__slide');
        var zoomed = slide && slide.classList.contains('is-zoomed');
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && Date.now() - t0 < 250) {
          var now = Date.now();
          if (now - lastTap < 300) { lastTap = 0; toggleZoom(slide, e.changedTouches[0].clientX, e.changedTouches[0].clientY); }
          else lastTap = now;
        } else if (!zoomed && dy > 80 && Math.abs(dx) < 40) {
          self.closeLightbox();
        }
        t0 = 0;
      }, { passive: true });

      this._lbKey = function (e) { if (e.key === 'Escape') self.closeLightbox(); };
    }

    openLightbox(index) {
      if (!this.lightbox) return;
      // Capture the page's own overflow only on a real closed->open transition; a double
      // open would otherwise capture our own 'hidden' and closing could never restore.
      if (this.lightbox.hidden) this._scrollLock = document.body.style.overflow;
      this.lightbox.hidden = false;
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._lbKey);
      this.lbIndex = Math.min(Math.max(index, 0), this.lbSlides.length - 1);
      this.loadLightboxImages();
      // Reading clientWidth after unhiding forces the layout the jump depends on.
      this.lbTrack.scrollLeft = this.lbIndex * this.lbTrack.clientWidth;
      this.syncLightboxCount();
    }

    /* Promote data-src/data-srcset to the real attributes for the photo in view and its
       two neighbours, so a swipe never lands on an empty frame while the rest of the set
       stays unrequested. */
    loadLightboxImages() {
      for (var i = this.lbIndex - 1; i <= this.lbIndex + 1; i++) {
        var slide = this.lbSlides[i];
        if (!slide) continue;
        var img = slide.querySelector('.gp-lightbox__img');
        if (!img || img.getAttribute('src')) continue;
        if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
        img.setAttribute('src', img.dataset.src);
      }
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.unzoomLightbox();
      this.lightbox.hidden = true;
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this._scrollLock || '';
      document.removeEventListener('keydown', this._lbKey);
      // Hand the photo the visitor ended on back to the gallery; activateMediaById
      // already keeps slides, thumbs and dots in step.
      var slide = this.lbSlides[this.lbIndex];
      if (slide) this.activateMediaById(slide.getAttribute('data-media-id'));
    }

    unzoomLightbox() {
      // Unconditional: the track is also locked during a pinch that never left fit.
      if (this.lbTrack) this.lbTrack.classList.remove('is-locked');
      this.lbSlides.forEach(function (s) {
        if (!s.classList.contains('is-zoomed')) return;
        s.classList.remove('is-zoomed');
        var img = s.querySelector('.gp-lightbox__img');
        if (img) img.style.width = '';
        // A leftover scroll offset would shift the photo inside its snap frame.
        s.scrollLeft = 0; s.scrollTop = 0;
      });
    }

    syncLightboxCount() {
      if (this.lbCount) this.lbCount.textContent = (this.lbIndex + 1) + ' / ' + this.lbSlides.length;
    }

    /* Scroll the mobile dot window so the active dot stays inside the 6-dot view,
       keeping two dots of context on the left where possible. */
    syncDots() {
      if (!this.dotsWin || this.dots.length <= DOTS_VISIBLE) return;
      var idx = 0;
      this.dots.forEach(function (d, i) { if (d.classList.contains('is-active')) idx = i; });
      var offset = Math.min(Math.max(idx - 2, 0), this.dots.length - DOTS_VISIBLE);
      this.dotsWin.scrollLeft = offset * DOT_STEP;
    }

    bindSwatches() {
      var self = this;
      this.swatches.forEach(function (sw) {
        sw.addEventListener('click', function () {
          self.selected[self.colorIdx] = sw.getAttribute('data-value');
          var mediaId = sw.getAttribute('data-media-id');
          if (mediaId) self.activateMediaById(mediaId);
          self.update();
        });
      });
    }

    bindSizes() {
      var self = this;
      this.sizeBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.classList.contains('is-soldout')) { /* allow select but stays disabled ATC */ }
          self.selected[self.sizeIdx] = b.getAttribute('data-value');
          self.update();
        });
      });
    }

    update() {
      var self = this;
      // color label ("— White")
      var colorLabel = this.querySelector('[data-gp-color-label]');
      if (colorLabel && this.colorIdx > -1) colorLabel.textContent = '— ' + this.selected[this.colorIdx];
      // swatch selected state
      this.swatches.forEach(function (sw) {
        sw.classList.toggle('is-selected', self.colorIdx > -1 && sw.getAttribute('data-value') === self.selected[self.colorIdx]);
      });
      // size selected + availability
      this.sizeBtns.forEach(function (b) {
        var val = b.getAttribute('data-value');
        b.classList.toggle('is-selected', self.sizeIdx > -1 && val === self.selected[self.sizeIdx]);
        b.classList.toggle('is-soldout', self.sizeIdx > -1 && !self.availableForOption(self.sizeIdx, val));
      });

      var variant = this.variantFor(this.selected);
      this.updateUrgency(variant);
      // update hidden id + price + button
      if (variant) {
        if (this.idInput) this.idInput.value = variant.id;
        if (this.priceSale) this.priceSale.innerHTML = variant.price_formatted;
        if (this.priceCompare) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            this.priceCompare.innerHTML = variant.compare_at_price_formatted;
            this.priceCompare.style.display = '';
          } else {
            this.priceCompare.style.display = 'none';
          }
        }
        if (this.atcBtn) {
          if (variant.available) {
            this.atcBtn.disabled = false;
            this.atcBtn.querySelector('[data-gp-atc-text]').textContent = this.atcBtn.getAttribute('data-add-label') || 'Add to cart';
          } else {
            this.atcBtn.disabled = true;
            this.atcBtn.querySelector('[data-gp-atc-text]').textContent = this.atcBtn.getAttribute('data-soldout-label') || 'Sold out';
          }
        }
        this.syncSticky(variant);
      } else {
        if (this.atcBtn) {
          this.atcBtn.disabled = true;
          this.atcBtn.querySelector('[data-gp-atc-text]').textContent = this.atcBtn.getAttribute('data-unavailable-label') || 'Unavailable';
        }
      }
    }

    /* ---- per-variant stock urgency ----
       The hash lives in snippets/gp-stock-signal.liquid and arrives as data-stock-offset; here we only
       rotate it by the variant's position, exactly like the Liquid does, so switching a size client-side
       shows the same line the server would have rendered (and the cart drawer will repeat). */
    updateUrgency(variant) {
      var el = this.urgencyEl;
      if (!el || !variant) return;
      var idx = this.variants.findIndex(function (v) { return v.id === variant.id; });
      var k = (parseInt(el.getAttribute('data-stock-offset'), 10) || 0) + (idx < 0 ? 0 : idx);
      var state, text;
      if (!variant.available) {
        state = 'sold';
        text = el.getAttribute('data-text-sold');
      } else {
        var ge = parseInt(el.getAttribute('data-green-every'), 10) || 4;
        var ml = parseInt(el.getAttribute('data-max-left'), 10) || 3;
        if (k % ge === 0) {
          state = 'green';
          text = el.getAttribute('data-text-green');
        } else {
          state = 'low';
          text = (el.getAttribute('data-text-low') || '').replace('[count]', (k % ml) + 1);
        }
      }
      el.classList.remove('is-low', 'is-green', 'is-sold');
      el.classList.add('is-' + state);
      var t = el.querySelector('.gp-urgency__text');
      if (t) t.textContent = text;
    }

    /* ---- rating badge under the title jumps to the reviews section ----
       Silently does nothing when that section is hidden, so the badge never scrolls to the wrong place. */
    bindRatingLink() {
      var link = this.querySelector('[data-gp-rating-link]');
      if (!link) return;
      link.addEventListener('click', function () {
        var target = document.getElementById('gp-reviews');
        if (!target || target.offsetParent === null) return;
        var from = window.scrollY;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Some browsers (and reduced-motion settings) ignore smooth scrolling entirely; jump instead of
        // leaving the shopper where they were.
        setTimeout(function () {
          if (Math.abs(window.scrollY - from) < 4) target.scrollIntoView({ block: 'start' });
        }, 350);
      });
    }

    /* ---- desktop sticky gallery ----
       Mode "top": the photo stops right under the sticky header and holds while the info column keeps
       scrolling — the reference behaviour. The header is measured live (it is 116px here, not the 90 this
       file used to assume, and it differs per breakpoint), so the photo never slides underneath it.
       Mode "full": pin at a negative offset instead, so a portrait photo taller than the screen first
       scrolls fully into view and only then holds — nothing is cut off, but it starts later.
       Either way sticky ends at the bottom of the grid, i.e. when the description and accordions are done. */
    bindStickyGallery() {
      if (!this.gallery) return;
      var self = this;
      var header = document.querySelector('.gp-header');

      // Sticky can never hold longer than the column beside it is tall, and most descriptions are still
      // shorter than a portrait photo (measured: 625 of 1100 products). Reserving a minimum below the info
      // column gives every product a real hold; it switches itself off as soon as the text is long enough.
      this.syncStickyHold = function () {
        if (!self.info) return;
        var hold = parseInt(self.gallery.getAttribute('data-sticky-hold'), 10);
        if (isNaN(hold)) hold = STICKY_HOLD;
        self.info.style.minHeight = '';
        if (window.innerWidth < 1000 || hold <= 0) return;
        var needed = self.gallery.offsetHeight + hold;
        if (self.info.offsetHeight < needed) self.info.style.minHeight = needed + 'px';
      };

      this.syncStickyTop = function () {
        // Only reserve room for a header that actually stays on screen. This one carries
        // position:sticky but sits in a section exactly its own height, so it has no range and
        // scrolls away like any other block — reserving 116px for it left a white band above the
        // pinned photo instead of stopping it at the top of the window.
        var headroom = 0;
        if (header) {
          var pos = getComputedStyle(header).position;
          var box = header.parentElement;
          if (pos === 'fixed') headroom = header.offsetHeight;
          else if (pos === 'sticky' && box && box.offsetHeight - header.offsetHeight > 40) headroom = header.offsetHeight;
        }
        var gap = parseInt(self.gallery.getAttribute('data-sticky-gap'), 10);
        if (isNaN(gap)) gap = STICKY_GAP;
        var top;
        if (self.gallery.getAttribute('data-sticky-mode') === 'full') {
          var h = self.gallery.offsetHeight;
          var room = window.innerHeight - headroom - gap - STICKY_BOTTOM;
          top = h <= room ? headroom + gap : window.innerHeight - h - STICKY_BOTTOM;
        } else {
          top = headroom + gap;
        }
        self.gallery.style.setProperty('--gp-sticky-top', Math.round(top) + 'px');
        self.syncStickyHold();
      };
      // On a resize both the viewport and the gallery height change; measuring in the next frame
      // means we read the settled values once instead of twice mid-layout. Cancel-and-reschedule rather
      // than skip: a background tab never runs its frame callback, and a plain guard would then stay
      // armed forever and freeze the offset at whatever the last visible layout was.
      var schedule = function () {
        if (self._stickyRaf) cancelAnimationFrame(self._stickyRaf);
        self._stickyRaf = requestAnimationFrame(function () { self._stickyRaf = 0; self.syncStickyTop(); });
      };
      this.syncStickyTop();
      document.addEventListener('visibilitychange', function () { if (!document.hidden) schedule(); });
      window.addEventListener('resize', schedule);
      // Images arrive late and slides differ in height, so track the real boxes.
      if (window.ResizeObserver) {
        this._galleryRO = new ResizeObserver(schedule);
        this._galleryRO.observe(this.gallery);
        if (header) this._galleryRO.observe(header);
      }
      // Description images only settle the info column once they are in; the info column itself is not
      // observed because we write its min-height, and watching what you set invites a feedback loop.
      window.addEventListener('load', schedule);
    }

    /* ---- sticky ATC (G&P style: option dropdowns + add button, no thumb/title/price) ---- */
    bindSticky() {
      this.sticky = document.querySelector('[data-gp-sticky-for="' + this.id + '"]');
      if (!this.sticky) return;
      var self = this;
      var anchor = this.atcBtn ? this.atcBtn.closest('.gp-buybtns') || this.atcBtn : this.querySelector('.gp-product__info');

      // sticky ATC triggers the main form submit (theme product-form handles AJAX + drawer)
      this.stickyBtn = this.sticky.querySelector('[data-gp-sticky-add]');
      if (this.stickyBtn) this.stickyBtn.addEventListener('click', function () {
        if (self.stickyBtn.disabled) return;
        if (self.form) self.form.requestSubmit ? self.form.requestSubmit() : self.form.submit();
      });

      // sticky option selects mirror the main selection (generic per option index)
      this.stickySelects = Array.prototype.slice.call(this.sticky.querySelectorAll('[data-gp-sticky-opt]'));
      this.stickySelects.forEach(function (sel) {
        var idx = parseInt(sel.getAttribute('data-gp-sticky-opt'), 10);
        sel.addEventListener('change', function () {
          self.selected[idx] = sel.value;
          if (idx === self.colorIdx) {
            var sw = self.swatches.find(function (x) { return x.getAttribute('data-value') === sel.value; });
            if (sw && sw.getAttribute('data-media-id')) self.activateMediaById(sw.getAttribute('data-media-id'));
          }
          self.update();
        });
      });

      // The bar is fixed to the bottom, so without reserved space it lies on top of the last thing on the
      // page — measured on mobile: a 128px bar covering the footer's payment icons and copyright.
      var reserve = function () {
        document.body.style.paddingBottom = self.sticky.offsetHeight + 'px';
      };
      reserve();
      window.addEventListener('resize', reserve);
      if (window.ResizeObserver) new ResizeObserver(reserve).observe(this.sticky);

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { self.sticky.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0); });
      }, { threshold: 0 });
      io.observe(anchor);

      // Optional: drop the bar altogether once the footer shows up.
      if (this.sticky.getAttribute('data-hide-at-footer') === 'true') {
        var footer = document.querySelector('.gp-footer') || document.querySelector('footer');
        if (footer) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (e) { self.sticky.classList.toggle('is-at-footer', e.isIntersecting); });
          }, { threshold: 0 }).observe(footer);
        }
      }
    }

    syncSticky(variant) {
      if (!this.sticky) return;
      var self = this;
      // mirror the selected option values into the sticky dropdowns
      if (this.stickySelects) {
        this.stickySelects.forEach(function (sel) {
          var idx = parseInt(sel.getAttribute('data-gp-sticky-opt'), 10);
          if (self.selected[idx] != null) sel.value = self.selected[idx];
        });
      }
      // reflect availability on the sticky button
      if (this.stickyBtn) {
        if (variant && variant.available) {
          this.stickyBtn.disabled = false;
          this.stickyBtn.textContent = this.stickyBtn.getAttribute('data-add-label') || 'Add to cart';
        } else {
          this.stickyBtn.disabled = true;
          this.stickyBtn.textContent = this.stickyBtn.getAttribute('data-soldout-label') || 'Sold out';
        }
      }
    }
  }

  if (!customElements.get('gp-product')) customElements.define('gp-product', GpProduct);

  /* Collapsible accordions (delegated) */
  document.addEventListener('click', function (e) {
    var head = e.target.closest('.gp-collapsible__head');
    if (!head) return;
    var item = head.closest('.gp-collapsible');
    if (!item) return;
    var open = item.classList.toggle('is-open');
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
})();
